import type { OrganizationSummary } from './types';
import { fold, type SortDirection } from './sorting';

/**
 * Finding and ordering centres, on the platform administrator's front page.
 *
 * Done here rather than on the server: a deployment has tens of centres, not
 * thousands, and they all arrive in one response already. Filtering in the
 * browser is instant and costs no round trip, and a list this size sorted in
 * SQL would only mean waiting for the network to reorder eleven rows. If a
 * deployment ever outgrows one response, this is the thing to move — but the
 * server would need paging first, and it does not have it.
 */

export type SortKey = 'name' | 'handle' | 'status' | 'instructors' | 'students' | 'created';

/** Name or handle, anywhere in either. */
export function searchCentres(
  centres: OrganizationSummary[],
  query: string,
): OrganizationSummary[] {
  const needle = fold(query);
  if (!needle) return centres;
  return centres.filter(
    (centre) => fold(centre.name).includes(needle) || fold(centre.slug).includes(needle),
  );
}

const compare = (a: OrganizationSummary, b: OrganizationSummary, key: SortKey): number => {
  switch (key) {
    case 'name':
      return a.name.localeCompare(b.name);
    case 'handle':
      return a.slug.localeCompare(b.slug);
    case 'status':
      // Suspended first when descending, which is the question somebody sorting
      // by status is asking: which of these is switched off?
      return Number(a.isActive) - Number(b.isActive);
    case 'instructors':
      return a.members.instructors - b.members.instructors;
    case 'students':
      return a.members.students - b.members.students;
    case 'created':
      return a.createdAt.localeCompare(b.createdAt);
  }
};

/**
 * Sorted, and never ambiguously so: centres that tie on the chosen column fall
 * back to their name, so the list does not shuffle when it is re-sorted by
 * something half of them share — which is most of these columns.
 */
export function sortCentres(
  centres: OrganizationSummary[],
  key: SortKey,
  direction: SortDirection,
): OrganizationSummary[] {
  const sign = direction === 'asc' ? 1 : -1;
  return [...centres].sort((a, b) => {
    const first = compare(a, b, key);
    if (first !== 0) return first * sign;
    return a.name.localeCompare(b.name);
  });
}

export const arrangeCentres = (
  centres: OrganizationSummary[],
  options: { query: string; key: SortKey; direction: SortDirection },
): OrganizationSummary[] =>
  sortCentres(searchCentres(centres, options.query), options.key, options.direction);
